import { GameState, Facing, IntruderType, BuildingType, Particle } from './types';
import {
  TILE, MAP_COLS, MAP_ROWS,
  COL_GRASS, COL_GRASS_ALT, COL_PATH, COL_PATH_ALT,
  COL_ROBOT_BODY, COL_ROBOT_HEAD, COL_ROBOT_EYE, COL_ROBOT_PUPIL, COL_ROBOT_ANTENNA,
  COL_TREE_TRUNK, COL_TREE_CANOPY, COL_TREE_STUMP,
  COL_SLIME, COL_SLIME_EYE,
  COL_WINDUP_BODY, COL_WINDUP_KEY,
  COL_VENDOR, COL_VENDOR_BODY,
  COL_WALL, COL_HOUSE, COL_HOUSE_ROOF, COL_STATION,
  PLAYER_SIZE,
} from './constants';

export function render(ctx: CanvasRenderingContext2D, state: GameState, canvasW: number, canvasH: number): void {
  ctx.save();
  ctx.clearRect(0, 0, canvasW, canvasH);

  // Camera transform
  ctx.translate(-state.camX, -state.camY);

  // Draw ground
  drawGround(ctx, state);

  // Collect all Y-sortable entities
  const entities: { y: number; draw: () => void }[] = [];

  // Trees
  for (const tree of state.trees) {
    const ty = tree.gridY * TILE + TILE;
    entities.push({ y: ty, draw: () => drawTree(ctx, tree.gridX, tree.gridY, tree.hp, tree.shakeTimer, tree.regrowTimer) });
  }

  // Vendor
  entities.push({
    y: state.vendorY * TILE + TILE,
    draw: () => drawVendor(ctx, state.vendorX * TILE + TILE / 2, state.vendorY * TILE + TILE / 2, state.totalTime),
  });

  // Upgrade station
  entities.push({
    y: state.stationGridY * TILE + TILE,
    draw: () => drawStation(ctx, state.stationGridX, state.stationGridY, state.baseLevel),
  });

  // Buildings
  for (const b of state.buildings) {
    entities.push({
      y: b.gridY * TILE + TILE,
      draw: () => drawBuilding(ctx, b.gridX, b.gridY, b.type),
    });
  }

  // Intruders
  for (const intruder of state.intruders) {
    if (!intruder.alive) continue;
    entities.push({
      y: intruder.y,
      draw: () => drawIntruder(ctx, intruder),
    });
  }

  // Player
  entities.push({
    y: state.player.y,
    draw: () => drawPlayer(ctx, state),
  });

  // Sort by Y and draw
  entities.sort((a, b) => a.y - b.y);
  for (const e of entities) {
    e.draw();
  }

  // Projectiles
  for (const proj of state.projectiles) {
    drawProjectile(ctx, proj.x, proj.y, proj.dx, proj.dy);
  }

  // Particles
  drawParticles(ctx, state.particles);

  // Build ghost
  if (state.buildMode && state.buildSelection) {
    drawBuildGhost(ctx, state.buildGhostX, state.buildGhostY, state.buildSelection);
  }

  ctx.restore();
}

function drawGround(ctx: CanvasRenderingContext2D, state: GameState): void {
  for (let r = 0; r < MAP_ROWS; r++) {
    for (let c = 0; c < MAP_COLS; c++) {
      const tile = state.ground[r][c];
      const checker = (r + c) % 2 === 0;
      if (tile === 'path') {
        ctx.fillStyle = checker ? COL_PATH : COL_PATH_ALT;
      } else {
        ctx.fillStyle = checker ? COL_GRASS : COL_GRASS_ALT;
      }
      ctx.fillRect(c * TILE, r * TILE, TILE, TILE);
    }
  }
}

function drawPlayer(ctx: CanvasRenderingContext2D, state: GameState): void {
  const p = state.player;
  const x = p.x;
  const y = p.y;
  const size = PLAYER_SIZE;

  ctx.save();
  ctx.translate(x, y);

  // Chop animation - slight rotation
  if (p.isChopping) {
    const progress = p.chopTimer / 0.2;
    const angle = Math.sin(progress * Math.PI) * 0.3;
    ctx.rotate(angle);
  }

  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.2)';
  ctx.beginPath();
  ctx.ellipse(0, size / 2 - 4, size / 2 - 4, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  // Body (rounded rectangle)
  const bodyW = size * 0.7;
  const bodyH = size * 0.6;
  ctx.fillStyle = COL_ROBOT_BODY;
  roundRect(ctx, -bodyW / 2, -bodyH / 2 + 4, bodyW, bodyH, 6);
  ctx.fill();

  // Head
  const headR = size * 0.3;
  ctx.fillStyle = COL_ROBOT_HEAD;
  ctx.beginPath();
  ctx.arc(0, -size * 0.2, headR, 0, Math.PI * 2);
  ctx.fill();

  // Antenna
  ctx.strokeStyle = COL_ROBOT_ANTENNA;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.2 - headR);
  ctx.lineTo(0, -size * 0.2 - headR - 8);
  ctx.stroke();
  ctx.fillStyle = COL_ROBOT_ANTENNA;
  ctx.beginPath();
  ctx.arc(0, -size * 0.2 - headR - 10, 3, 0, Math.PI * 2);
  ctx.fill();

  // Eyes
  const eyeSpacing = 7;
  const eyeY = -size * 0.22;
  // Pupil offset based on facing
  let pupilDx = 0, pupilDy = 0;
  switch (p.facing) {
    case Facing.Left: pupilDx = -2; break;
    case Facing.Right: pupilDx = 2; break;
    case Facing.Up: pupilDy = -2; break;
    case Facing.Down: pupilDy = 2; break;
  }

  for (const side of [-1, 1]) {
    // White
    ctx.fillStyle = COL_ROBOT_EYE;
    ctx.beginPath();
    ctx.arc(side * eyeSpacing, eyeY, 5, 0, Math.PI * 2);
    ctx.fill();
    // Pupil
    ctx.fillStyle = COL_ROBOT_PUPIL;
    ctx.beginPath();
    ctx.arc(side * eyeSpacing + pupilDx, eyeY + pupilDy, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Mouth (small smile)
  ctx.strokeStyle = COL_ROBOT_PUPIL;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(0, eyeY + 9, 4, 0.2, Math.PI - 0.2);
  ctx.stroke();

  ctx.restore();
}

function drawTree(ctx: CanvasRenderingContext2D, gridX: number, gridY: number, hp: number, shakeTimer: number, regrowTimer: number): void {
  const cx = gridX * TILE + TILE / 2;
  const cy = gridY * TILE + TILE / 2;

  ctx.save();
  ctx.translate(cx, cy);

  // Shake
  if (shakeTimer > 0) {
    const offset = Math.sin(shakeTimer * 40) * 3;
    ctx.translate(offset, 0);
  }

  if (hp > 0) {
    // Trunk
    ctx.fillStyle = COL_TREE_TRUNK;
    ctx.fillRect(-6, -4, 12, 28);

    // Canopy
    ctx.fillStyle = COL_TREE_CANOPY;
    ctx.beginPath();
    ctx.arc(0, -12, 20, 0, Math.PI * 2);
    ctx.fill();

    // Highlight
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.beginPath();
    ctx.arc(-5, -16, 8, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // Stump
    ctx.fillStyle = COL_TREE_STUMP;
    ctx.fillRect(-8, 6, 16, 12);
    ctx.fillStyle = COL_TREE_TRUNK;
    ctx.fillRect(-6, 8, 12, 8);

    // Regrow progress indicator
    if (regrowTimer > 0) {
      const progress = 1 - regrowTimer / 20;
      if (progress > 0.5) {
        ctx.fillStyle = COL_TREE_CANOPY;
        ctx.globalAlpha = (progress - 0.5) * 2;
        ctx.beginPath();
        ctx.arc(0, 0, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }
  }

  ctx.restore();
}

function drawVendor(ctx: CanvasRenderingContext2D, x: number, y: number, time: number): void {
  ctx.save();
  ctx.translate(x, y);

  // Bobbing animation
  const bob = Math.sin(time * 2) * 2;

  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.2)';
  ctx.beginPath();
  ctx.ellipse(0, 20, 16, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  // Body
  ctx.fillStyle = COL_VENDOR_BODY;
  roundRect(ctx, -14, -10 + bob, 28, 28, 5);
  ctx.fill();

  // Head
  ctx.fillStyle = COL_VENDOR;
  ctx.beginPath();
  ctx.arc(0, -14 + bob, 14, 0, Math.PI * 2);
  ctx.fill();

  // Eyes
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(-5, -16 + bob, 4, 0, Math.PI * 2);
  ctx.arc(5, -16 + bob, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#1a1a2e';
  ctx.beginPath();
  ctx.arc(-5, -16 + bob, 2, 0, Math.PI * 2);
  ctx.arc(5, -16 + bob, 2, 0, Math.PI * 2);
  ctx.fill();

  // Dollar sign on body
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('$', 0, 4 + bob);

  // Label
  ctx.fillStyle = '#ffd700';
  ctx.font = 'bold 10px sans-serif';
  ctx.fillText('SELL', 0, -32 + bob);

  ctx.restore();
}

function drawStation(ctx: CanvasRenderingContext2D, gridX: number, gridY: number, baseLevel: number): void {
  const x = gridX * TILE;
  const y = gridY * TILE;

  ctx.save();

  // Workbench body
  ctx.fillStyle = COL_STATION;
  roundRect(ctx, x + 4, y + 16, TILE - 8, TILE - 20, 4);
  ctx.fill();

  // Table top
  ctx.fillStyle = '#a0845a';
  ctx.fillRect(x + 2, y + 14, TILE - 4, 6);

  // Level stars
  ctx.fillStyle = '#ffd700';
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  const stars = '★'.repeat(Math.min(baseLevel, 5));
  ctx.fillText(stars, x + TILE / 2, y + 10);

  // Label
  ctx.fillStyle = '#4a2c0a';
  ctx.font = 'bold 9px sans-serif';
  ctx.fillText('UPGRADE', x + TILE / 2, y + TILE - 4);

  ctx.restore();
}

function drawBuilding(ctx: CanvasRenderingContext2D, gridX: number, gridY: number, type: BuildingType): void {
  const x = gridX * TILE;
  const y = gridY * TILE;

  ctx.save();

  switch (type) {
    case BuildingType.Wall:
      ctx.fillStyle = COL_WALL;
      ctx.fillRect(x + 2, y + 2, TILE - 4, TILE - 4);
      // Brick pattern
      ctx.strokeStyle = '#6b7b7c';
      ctx.lineWidth = 1;
      for (let row = 0; row < 4; row++) {
        const ry = y + 4 + row * 14;
        ctx.beginPath();
        ctx.moveTo(x + 2, ry);
        ctx.lineTo(x + TILE - 2, ry);
        ctx.stroke();
        const offset = row % 2 === 0 ? 0 : TILE / 4;
        for (let col = 0; col < 3; col++) {
          const rx = x + offset + col * (TILE / 2);
          if (rx > x + 2 && rx < x + TILE - 2) {
            ctx.beginPath();
            ctx.moveTo(rx, ry);
            ctx.lineTo(rx, ry + 14);
            ctx.stroke();
          }
        }
      }
      break;

    case BuildingType.House:
      // Walls
      ctx.fillStyle = COL_HOUSE;
      ctx.fillRect(x + 8, y + 24, TILE - 16, TILE - 28);
      // Roof
      ctx.fillStyle = COL_HOUSE_ROOF;
      ctx.beginPath();
      ctx.moveTo(x + 4, y + 26);
      ctx.lineTo(x + TILE / 2, y + 6);
      ctx.lineTo(x + TILE - 4, y + 26);
      ctx.closePath();
      ctx.fill();
      // Door
      ctx.fillStyle = '#8b4513';
      ctx.fillRect(x + TILE / 2 - 5, y + TILE - 16, 10, 14);
      // Window
      ctx.fillStyle = '#87ceeb';
      ctx.fillRect(x + 14, y + 30, 8, 8);
      break;

    case BuildingType.UpgradeStation:
      drawStation(ctx, gridX, gridY, 0);
      break;
  }

  ctx.restore();
}

function drawIntruder(ctx: CanvasRenderingContext2D, intruder: { x: number; y: number; type: IntruderType; boopTimer: number; animTimer: number }): void {
  ctx.save();
  ctx.translate(intruder.x, intruder.y);

  // Fade out during boop
  if (intruder.boopTimer > 0) {
    ctx.globalAlpha = intruder.boopTimer / 0.4;
  }

  if (intruder.type === IntruderType.Slime) {
    // Squish animation
    const squish = Math.sin(intruder.animTimer * 4) * 0.15;
    const scaleX = 1 + squish;
    const scaleY = 1 - squish;
    ctx.scale(scaleX, scaleY);

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.beginPath();
    ctx.ellipse(0, 14, 14, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    // Body
    ctx.fillStyle = COL_SLIME;
    ctx.beginPath();
    ctx.ellipse(0, 0, 16, 14, 0, 0, Math.PI * 2);
    ctx.fill();

    // Highlight
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.beginPath();
    ctx.ellipse(-4, -5, 6, 4, -0.3, 0, Math.PI * 2);
    ctx.fill();

    // Goofy eyes
    ctx.fillStyle = COL_SLIME_EYE;
    ctx.beginPath();
    ctx.arc(-6, -3, 5, 0, Math.PI * 2);
    ctx.arc(6, -3, 5, 0, Math.PI * 2);
    ctx.fill();
    // Pupils (cross-eyed)
    ctx.fillStyle = '#1a1a2e';
    ctx.beginPath();
    ctx.arc(-4, -3, 2.5, 0, Math.PI * 2);
    ctx.arc(4, -3, 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Silly smile
    ctx.strokeStyle = '#7b3fa0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 2, 6, 0.2, Math.PI - 0.2);
    ctx.stroke();
  } else {
    // Wind-up Bot
    const bob = Math.sin(intruder.animTimer * 6) * 1.5;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.beginPath();
    ctx.ellipse(0, 16, 14, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    // Body (box)
    ctx.fillStyle = COL_WINDUP_BODY;
    roundRect(ctx, -14, -10 + bob, 28, 24, 4);
    ctx.fill();

    // Wind-up key (rotating)
    ctx.save();
    ctx.translate(16, -2 + bob);
    ctx.rotate(intruder.animTimer * 3);
    ctx.fillStyle = COL_WINDUP_KEY;
    ctx.fillRect(-2, -8, 4, 16);
    ctx.fillRect(-6, -10, 12, 4);
    ctx.restore();

    // Spiral eyes
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 1.5;
    for (const side of [-1, 1]) {
      ctx.save();
      ctx.translate(side * 6, -4 + bob);
      ctx.beginPath();
      for (let i = 0; i < 12; i++) {
        const angle = i * 0.5 + intruder.animTimer * 2;
        const r = i * 0.6;
        const px = Math.cos(angle) * r;
        const py = Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
      ctx.restore();
    }

    // Zigzag mouth
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-6, 6 + bob);
    for (let i = 0; i < 4; i++) {
      ctx.lineTo(-6 + i * 4 + 2, (i % 2 === 0 ? 9 : 4) + bob);
    }
    ctx.stroke();
  }

  ctx.restore();
}

function drawProjectile(ctx: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number): void {
  ctx.save();
  ctx.translate(x, y);

  // Rotate to face direction
  const angle = Math.atan2(dy, dx);
  ctx.rotate(angle);

  // Spear shape
  ctx.fillStyle = '#8b5a2b';
  ctx.fillRect(-12, -2, 20, 4);
  // Tip
  ctx.fillStyle = '#95a5a6';
  ctx.beginPath();
  ctx.moveTo(8, -4);
  ctx.lineTo(14, 0);
  ctx.lineTo(8, 4);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

function drawParticles(ctx: CanvasRenderingContext2D, particles: Particle[]): void {
  for (const p of particles) {
    if (p.life <= 0) continue;
    ctx.globalAlpha = Math.min(1, p.life / p.maxLife * 2);
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * (p.life / p.maxLife), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawBuildGhost(ctx: CanvasRenderingContext2D, gridX: number, gridY: number, type: BuildingType): void {
  ctx.save();
  ctx.globalAlpha = 0.5;
  drawBuilding(ctx, gridX, gridY, type);
  ctx.globalAlpha = 1;

  // Grid highlight
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 4]);
  ctx.strokeRect(gridX * TILE, gridY * TILE, TILE, TILE);
  ctx.setLineDash([]);
  ctx.restore();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
